import { apiRequest } from 'utils/request'

export const getReferenceParameters = ({ serialNumber }) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/reference-parameters`,
  })

export const getModules = ({ serialNumber }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}/modules`,
  })

export const getFleet = ({ leadSerialNumber, serialNumber }) =>
  apiRequest({
    // leadSerialNumber is set for fleet members; for the lead vehicle itself it is
    // absent, so fall back to serialNumber so the URI is never /vehicles/undefined/fleet.
    uri: `/vehicles/${leadSerialNumber || serialNumber}/fleet`,
  })

export const getRevisionHistory = ({ serialNumber }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}/revisions`,
  })

/**
 * Save added/removed/updated features and switches for a vehicle.
 * @param {{ serialNumber: string, body: object }} params
 */
export const updateVehicleFeatures = ({ serialNumber, body }) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/features/update`,
    method: 'POST',
    body,
  })

/**
 * Validate features against a vehicle's current configuration.
 * Used for both conflict detection (addedFeatures/removedFeatures body) and
 * switch-panel pre-check (addedSwitches body) — the body shape determines
 * which validation the backend performs.
 * @param {{ serialNumber: string, body: object }} params
 */
export const validateVehicleFeatures = ({ serialNumber, body }) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/features/validate`,
    method: 'POST',
    // readOnly: true marks this as a semantically-read POST so the default
    // error handler treats failures as silent reads rather than blocking
    // mutations. A conflict-check failure should not show "your changes may
    // not have been saved" — the user hasn't submitted anything.
    readOnly: true,
    body,
  })

export const getDetails = ({ serialNumber }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}`,
    method: 'GET',
  })

/**
 * HTTP 200 response code used by the backend to indicate a PTP validation
 * failure. The API returns 200 OK with this code in the response body rather
 * than a true HTTP error status. See API contract: PTP validation endpoint.
 *
 * This constant MUST remain digit-only. The comparison guards in this file use
 * `String(responseCode).toUpperCase()` for consistency, but `.toUpperCase()` is
 * a no-op for a pure-digit string like '424'. If PTP_ERROR_CODE ever gains
 * letters (e.g. '4XX'), both this constant and its comparison sites must be
 * updated together so the normalisation applies to both sides.
 */
const PTP_ERROR_CODE = '424'

/**
 * Custom error class for PTP (Parameter Transfer Protocol) failures.
 *
 * The backend returns a successful HTTP 200 with `result.code === PTP_ERROR_CODE`
 * when a PTP validation step fails. The service layer promotes this into a thrown
 * PtpError so that the apiCall saga's error handler chain can treat it like
 * any other error.
 *
 * The `ptpErrorHandler` in `sagas/errorHandlers/ptpErrorHandler.js` matches
 * on `err.name === 'PtpError'` and shows the PTP-specific error modal.
 */
export class PtpError extends Error {
  constructor(message, { code, description } = {}) {
    super(message)
    this.name = 'PtpError'
    this.code = code
    this.description = description
  }
}

export const getValidatePtp = ({ serialNumber }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}/validate`,
    method: 'GET',
  }).then((response) => {
    // The validate endpoint returns HTTP 200 with `result.code === PTP_ERROR_CODE`
    // when a PTP validation step fails — the same in-body error convention used by
    // saveVehicleReferenceParameters. Without this check, the 424-coded response
    // would land on the success path (GET_VALIDATE_PTP dispatched, no modal shown)
    // and the user would never be told that the vehicle failed PTP validation.
    //
    // We promote the application-level failure into a PtpError so that
    // ptpErrorHandler (registered in actions/truck.js) can show the PTP-specific
    // modal. The error is also pre-enriched with method/requestUri so that error
    // handlers receive the correct context even when apiRequest is mocked in tests.
    const code = String(response?.result?.code ?? '')
    if (code === PTP_ERROR_CODE) {
      const ptpErr = new PtpError(response.result.description, {
        code: response.result.code,
        description: response.result.description,
      })
      ptpErr.method = 'GET'
      ptpErr.requestUri = `/vehicles/${serialNumber}/validate`
      throw ptpErr
    }
    return response
  })

export const getRevisions = ({ serialNumber }) =>
  apiRequest(`/vehicles/${serialNumber}/revisions`)

export const getFeatures = ({ serialNumber }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}/features?includeRevisionQueryCounts=true`,
    method: 'GET',
  })

export const addExm = ({ serialNumber, body }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}/modules/exm1/add`,
    method: 'POST',
    body,
  })

export const addXmc = ({ serialNumber, body }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}/modules/add`,
    method: 'POST',
    body,
  })

export const updateExm = ({ serialNumber, moduleName, notes, size }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}/modules/${moduleName}/update`,
    method: 'POST',
    body: {
      moduleName,
      notes,
      size,
    },
  })

export const saveVehicleReferenceParameters = ({
  serialNumber,
  referenceParameters,
  ctpModels,
  notes,
  vehicleUri,
  updateFleet,
}) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/reference-parameters/add`,
    method: 'POST',
    body: {
      notes,
      serialNumber,
      referenceParameters,
      ctpModels,
      ...(vehicleUri !== undefined && { vehicleUri }),
      ...(updateFleet !== undefined && { updateFleet }),
    },
  }).then((response) => {
    // Normalize to uppercase string once so both guards use the same type.
    // The API may return code as a string ('424', 'OK') or occasionally a number;
    // normalising avoids type-inconsistent comparisons where String('424')==='424'
    // but numeric 200 !== 'OK' would incorrectly throw on a clean success.
    // Note: .toUpperCase() is a no-op for PTP_ERROR_CODE ('424') since it contains
    // only digits — it is applied uniformly here alongside the 'OK' check so the
    // normalisation is consistent regardless of which guard fires.
    const code = String(response?.result?.code ?? '').toUpperCase()
    if (code === PTP_ERROR_CODE) {
      const ptpErr = new PtpError(response.result.description, {
        code: response.result.code,
        description: response.result.description,
      })
      // Pre-set enrichment fields so error handlers receive the correct method /
      // requestUri even when apiRequest is mocked in unit tests (the mock
      // bypasses apiRequest's outer .catch which normally attaches these).
      ptpErr.method = 'POST'
      ptpErr.requestUri = `vehicles/${serialNumber}/reference-parameters/add`
      throw ptpErr
    }
    // Guard against non-OK application-level result codes that arrive inside a
    // 200 HTTP response. Without this check, any partial-failure or rejected-save
    // code (e.g. '400', '422') would pass straight through to apiCall's success
    // path, firing VEHICLE_REFERENCE_PARAMETERS_SAVED and the "Vehicle updated"
    // toast even though the RP save actually failed.
    //
    // API CONTRACT: 'OK' is the ONLY expected success sentinel for this endpoint.
    // The backend never returns '200', '201', 'CREATED', or any other success
    // string — only 'OK' on clean success, '424' for PTP failures (handled above),
    // and application-level error codes ('400', '422', etc.) for save failures.
    // If the backend contract changes, update this guard to match.
    //
    // Only throw for codes that are explicitly non-OK; absent or null codes
    // (which some endpoints return on clean success) are allowed through.
    if (code && code !== 'OK') {
      const err = new Error(
        response.result.description || `Save failed: ${response.result.code}`
      )
      err.code = response.result.code
      err.description = response.result.description
      // Pre-set enrichment fields so error handlers receive the correct method /
      // requestUri even when apiRequest is mocked in unit tests (the mock
      // bypasses apiRequest's outer .catch which normally attaches these).
      err.method = 'POST'
      err.requestUri = `vehicles/${serialNumber}/reference-parameters/add`
      throw err
    }
    return response
  })

export const getSolenoids = ({ ecpVariant }) =>
  apiRequest({
    uri: `modules/ecp/solenoids/${ecpVariant}`,
  })

export const updateEcp = ({ serialNumber, id, module }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}/modules/ecp/update`,
    method: 'POST',
    body: module,
  })

export const validateParFile = ({ serialNumber, isPreProdRequest = false }) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/par/validate?isPreProdRequest=${isPreProdRequest}`,
  })

export const downloadParFile = ({ serialNumber, isPreProdRequest = false }) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/par?isPreProdRequest=${isPreProdRequest}`,
    download: `${serialNumber}.zip`,
  })

export const getPowerTakeOff = ({ serialNumber }) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/pto`,
    method: 'GET',
  })

export const getPtoTemplatesMeta = ({ serialNumber }) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/pto/templates`,
    method: 'GET',
  })

export const getPtoTemplates = () =>
  apiRequest({
    uri: 'pto-templates',
    method: 'GET',
  })

export const getPtoTemplateById = ({ templateId }) =>
  apiRequest({
    uri: `pto-templates/${templateId}`,
    method: 'GET',
  })

export const getVehiclePtoTemplateById = ({ templateId, serialNumber }) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/pto-templates/${templateId}`,
    method: 'GET',
  })

export const updateFragments = ({ serialNumber, fragments, notes }) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/pto/update`,
    method: 'POST',
    body: { fragments, notes },
  })

export const createPtoTemplate = ({
  serialNumber,
  notes,
  templateName,
  templateDescription,
  domainList,
  isTemplateRounded,
}) =>
  apiRequest({
    uri: `vehicles/${serialNumber}/pto/templates/create`,
    method: 'POST',
    body: {
      name: templateName,
      description: templateDescription,
      notes,
      domainIds: domainList,
      ...(isTemplateRounded !== undefined ? { isTemplateRounded } : {}),
    },
  })

export const getEcpLinkedFeatures = ({ outputId, moduleType, serialNumber }) =>
  apiRequest({
    method: 'POST',
    readOnly: true,
    uri: `/features/search`,
    body: {
      startIndex: 1,
      count: 100,
      search: '',
      filters: [
        {
          schemaId: 'features.in.vehicle',
          operator: 'in',
          value: `${serialNumber}`,
        },
        {
          schemaId: 'feature.equals.moduleType',
          operator: 'equals',
          value: `${moduleType}`,
        },
        {
          schemaId: 'feature.contains.nativeOutputs',
          operator: 'contains',
          value: `${outputId}`,
        },
      ],
    },
  })

export const getVehicleComparisonData = ({ serialNumbers, vehicleType }) => {
  const serialNumbersString = serialNumbers
    .map((serialNumber) => encodeURIComponent(serialNumber))
    .join('&serials=')

  return apiRequest({
    uri: `/vehicles/compare?vehicleType=${vehicleType}&serials=${serialNumbersString}`,
    method: 'GET',
  })
}

export const trigger2DLayout = ({ serialNumber }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}/2d-layout`,
    method: 'POST',
  })

// No `/plus` segment — the endpoint is not mounted under one. Verified against
// both APIs: `/plus/vehicles/{sn}/fast-feature-switch-mapping` returns 404 while
// `/vehicles/{sn}/fast-feature-switch-mapping` reaches the handler, locally and on
// the deployed vit-dev environment. The version prefix is added by the proxy /
// UsePathBase, not here.
export const getFastFeatureSwitchMapping = ({ serialNumber }) =>
  apiRequest({
    uri: `/vehicles/${serialNumber}/fast-feature-switch-mapping`,
    method: 'GET',
    headers: { 'Cache-Control': 'no-cache' },
  })
